import type {
  OrgPace,
  TeamNorm,
  TeamPeriodStats,
  TeamDetailResponse,
  TeamInferenceResult,
  TeamInsightsResponse,
  TeamSummary,
} from "@aura/shared";
import { fmtDate, fmtNum, plural } from "~/lib/format";

// Arithmetic behind the Teams pages, kept out of the components so it is
// covered by model.test.ts.

/** Above this share of a team's reviews, the top two reviewers are a short bench. */
export const CONCENTRATION_THRESHOLD = 60;

/** Merged PRs per active person, or null when nobody was active. */
export function perPerson(prsMerged: number, activeCount: number): number | null {
  return activeCount > 0 ? Math.round((prsMerged / activeCount) * 10) / 10 : null;
}

/**
 * Percentage-point change for 0-100 shares: "+4 pts" ▲, "-2 pts" ▼, or
 * "no change". Empty when either side is missing.
 */
export function pointChange(
  current: number | null | undefined,
  prior: number | null | undefined,
): { change?: string; arrow?: string } {
  if (current === null || current === undefined || prior === null || prior === undefined) return {};
  const d = Math.round(current - prior);
  if (d === 0) return { change: "no change", arrow: "" };
  return { change: `${d > 0 ? "+" : ""}${d} pts`, arrow: d > 0 ? "▲" : "▼" };
}

/**
 * A team's merged PRs per active person as a share of the org's, where 100 is
 * the org's own rate. Both sides cover the same days, so holidays and short
 * months cancel out.
 */
export function paceIndex(
  team: Pick<TeamPeriodStats, "prsMerged" | "activeCount">,
  org: Pick<OrgPace, "prsMerged" | "activeCount">,
): number | null {
  if (team.activeCount <= 0 || org.activeCount <= 0 || org.prsMerged <= 0) return null;
  return Math.round((team.prsMerged / team.activeCount / (org.prsMerged / org.activeCount)) * 100);
}

/**
 * The org's change in merged PRs per person per working day, as a whole
 * percent. Per working day so a period with Labor Day in it doesn't read as
 * a slowdown.
 */
export function orgDailyChange(norm: TeamNorm): number | null {
  const rate = (side: "current" | "previous") => {
    const org = norm.org[side];
    const days = norm.calendar[side].workingDays;
    return org.activeCount > 0 && days > 0 ? org.prsMerged / org.activeCount / days : null;
  };
  const now = rate("current");
  const before = rate("previous");
  if (now === null || before === null || before === 0) return null;
  return Math.round((now / before - 1) * 100);
}

export interface MomentumRow {
  id: string;
  name: string;
  /** Pace index last period; null when the team had nobody working. */
  before: number | null;
  now: number;
  /** Index points gained on the org; null without a before. */
  change: number | null;
}

/**
 * Teams ordered by how much ground they gained on the org. A team that merged
 * more only because everyone did hasn't moved.
 */
export function momentumRows(teams: TeamSummary[], norm: TeamNorm): MomentumRow[] {
  const rows: MomentumRow[] = [];
  for (const t of teams) {
    const now = paceIndex(t.current, norm.org.current);
    if (now === null) continue;
    const before = paceIndex(t.previous, norm.org.previous);
    rows.push({ id: t.id, name: t.name, before, now, change: before === null ? null : now - before });
  }
  return rows.sort(
    (a, b) =>
      (a.change === null ? 1 : 0) - (b.change === null ? 1 : 0) ||
      (b.change ?? 0) - (a.change ?? 0) ||
      a.name.localeCompare(b.name),
  );
}

/** How far a value sits from a reference, as a ratio; null without a base. */
export function ratioTo(value: number | null, reference: number | null): number | null {
  if (value === null || reference === null || reference <= 0) return null;
  return value / reference;
}

export interface ConcentrationRow {
  id: string;
  name: string;
  share: number;
  prior: number | null;
  overThreshold: boolean;
}

/** Teams ranked by their top-two-reviewer share, highest first. Teams with no reviews drop out. */
export function concentrationRows(
  teams: TeamSummary[],
  threshold = CONCENTRATION_THRESHOLD,
): ConcentrationRow[] {
  return teams
    .filter((t) => t.current.reviewConcentration !== null && t.current.reviewsGiven > 0)
    .map((t) => ({
      id: t.id,
      name: t.name,
      share: t.current.reviewConcentration ?? 0,
      prior: t.previous.reviewConcentration,
      overThreshold: (t.current.reviewConcentration ?? 0) > threshold,
    }))
    .sort((a, b) => b.share - a.share || a.name.localeCompare(b.name));
}

export interface CrossTeamMatrix {
  /** Teams in display order: those that give or get cross-team reviews first. */
  teams: Array<{ id: string; name: string }>;
  /** count(from reviewer team -> to author team) */
  cell: (fromId: string, toId: string) => number;
  given: Map<string, number>;
  received: Map<string, number>;
  total: number;
  /** The largest single flow, if any. */
  top: { from: string; to: string; count: number } | null;
}

export function crossTeamMatrix(
  teams: Array<{ id: string; name: string }>,
  edges: TeamInsightsResponse["crossTeamReviews"],
): CrossTeamMatrix {
  const known = new Set(teams.map((t) => t.id));
  const counts = new Map<string, number>();
  const given = new Map<string, number>();
  const received = new Map<string, number>();
  let total = 0;
  let top: CrossTeamMatrix["top"] = null;
  for (const e of edges) {
    if (e.fromTeamId === e.toTeamId || e.count <= 0) continue;
    if (!known.has(e.fromTeamId) || !known.has(e.toTeamId)) continue;
    const k = `${e.fromTeamId}>${e.toTeamId}`;
    const n = (counts.get(k) ?? 0) + e.count;
    counts.set(k, n);
    given.set(e.fromTeamId, (given.get(e.fromTeamId) ?? 0) + e.count);
    received.set(e.toTeamId, (received.get(e.toTeamId) ?? 0) + e.count);
    total += e.count;
    if (!top || n > top.count) top = { from: e.fromTeamId, to: e.toTeamId, count: n };
  }
  const flow = (id: string) => (given.get(id) ?? 0) + (received.get(id) ?? 0);
  const ordered = [...teams].sort((a, b) => flow(b.id) - flow(a.id) || a.name.localeCompare(b.name));
  return {
    teams: ordered,
    cell: (from, to) => counts.get(`${from}>${to}`) ?? 0,
    given,
    received,
    total,
    top,
  };
}

/** Work categories across every team, largest first, by merged PR count. */
export function workMixTotals(
  teams: TeamSummary[],
): Array<{ category: string; label: string; count: number }> {
  const totals = new Map<string, { label: string; count: number }>();
  for (const t of teams) {
    for (const c of t.current.workMix) {
      const cur = totals.get(c.category) ?? { label: c.label, count: 0 };
      cur.count += c.count;
      totals.set(c.category, cur);
    }
  }
  return [...totals.entries()]
    .map(([category, v]) => ({ category, ...v }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count);
}

/** Weekly merged PRs in date order, prior window first. */
export function weeklySeries(weekly: TeamDetailResponse["weekly"]) {
  return [...weekly].sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}

/** Members who carry work for another team, and how many are pinned. */
export function rosterCounts(members: TeamDetailResponse["members"]) {
  return {
    shared: members.filter((m) => m.shared).length,
    pinned: members.filter((m) => m.source === "pinned").length,
    leads: members.filter((m) => m.isLead).length,
  };
}

/**
 * A team key worth showing beside the name: a JIRA-style project key such
 * as "TOPS" or "DA5". Manual teams fall back to their id, which isn't.
 */
export function displayKey(key: string): string | null {
  return /^[A-Z][A-Z0-9_]{0,9}$/.test(key) ? key : null;
}

/**
 * A member's share of work on this team. Null means no measured share:
 * placed through the review graph, or pinned with no work in the window.
 */
export function shareText(share: number | null, source: "inferred" | "pinned"): string {
  if (share === null) return source === "pinned" ? "–" : "via reviews";
  return `${Math.round(share * 100)}%`;
}

/** Plain-words summary of an inference run, for the admin banner. */
export function inferenceSummary(r: TeamInferenceResult): string {
  return `Worked out ${plural(r.teams, "team")} on ${fmtDate(r.inferredAt)}: ${fmtNum(r.assigned)} people placed, ${fmtNum(r.shared)} shared, ${fmtNum(r.unassigned)} unassigned. ${plural(r.pinnedKept, "pinned person", "pinned people")} kept as set.`;
}

/** Leads first, then by name. Members are listed, not ranked. */
export function rosterOrder(members: TeamDetailResponse["members"]) {
  return [...members].sort(
    (a, b) => Number(b.isLead) - Number(a.isLead) || a.displayName.localeCompare(b.displayName),
  );
}
